import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { magicLampsAnimEx } from './MagicLampsAnim';

interface MagicLampsProgressBarOpts extends ViewOpts {
  checkpoints: Record<number, { first: number; last: number }>;
  numRounds: number;
  round: number;
}

type CheckpointRange = { first: number; last: number };
type CheckpointItem = { mark: ImageScaleView; first: number; last: number };

export class MagicLampsProgressBar extends View {
  private base: ImageScaleView;
  private fill: ImageScaleView;
  private icon: ImageScaleView;
  private label: LangBitmapFontTextView;
  private minFillWidth = 35;
  private maxFillWidth = 465;
  private numRounds = 30;
  private _ratio = 0;
  private _round = 0;
  private marks: Record<string, CheckpointItem> = {};

  constructor(opts: MagicLampsProgressBarOpts) {
    super({ ...opts });

    const width = 557;
    const height = 67;

    this.base = new ImageScaleView({
      superview: this,
      width,
      height,
      image: `assets/ui/match2/magiclamps/magiclamps_bar_base.png`,
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 40, right: 40 },
      },
    });

    this.fill = new ImageScaleView({
      superview: this,
      width: 475,
      height: height - 9,
      image: `assets/ui/match2/magiclamps/magiclamps_bar_fill.png`,
      scaleMethod: '3slice',
      x: 3,
      y: 3,
      sourceSlices: {
        horizontal: { left: 28, right: 8 },
      },
    });

    this.label = new LangBitmapFontTextView({
      superview: this,
      x: width / 2,
      y: height - 15,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 22,
      font: 'Title',
      wordWrap: true,
    });
    this.label.localeText = () => '5/30';

    this.icon = new ImageScaleView({
      superview: this,
      image: `assets/ui/match2/magiclamps/magiclamps_counter.png`,
      width: 30,
      height: 35,
      centerAnchor: true,
      centerOnOrigin: true,
    });

    this.numRounds = opts.numRounds;

    for (const [checkpoint, round] of Object.entries(opts.checkpoints)) {
      this.createMark(checkpoint, round);
    }

    this.ratio = 0;
    this.round = opts.round;
  }

  public get ratio() {
    return this._ratio;
  }

  public set ratio(value: number) {
    this._ratio = Math.max(0, value);
    this.fill.style.width = this.ratioToX(this._ratio);
  }

  public get round() {
    return this._round;
  }

  public set round(value: number) {
    this._round = Math.max(0, value);
    this.label.localeText = () => `${this._round}/${this.numRounds}`;
    this.ratio = this.roundToRatio(this._round);
    this.icon.updateOpts({
      x: this.label.style.x - 50,
      y: this.label.style.y + 10,
    });
    this.updateMarks();
  }

  private ratioToX(ratio: number) {
    return this.minFillWidth + (this.maxFillWidth - this.minFillWidth) * ratio;
  }

  private roundToRatio(round: number) {
    return (round - 1) / (this.numRounds - 1);
  }

  private createMark(key: string, stage: CheckpointRange) {
    const checkpoint = key.padStart(2, '0');
    const mark = new ImageScaleView({
      superview: this,
      centerAnchor: true,
      centerOnOrigin: true,
      x: this.ratioToX(this.roundToRatio(stage.first)),
    });
    this.marks[checkpoint] = { mark, ...stage };
  }

  private updateMarks(complete = false) {
    for (const [checkpoint, item] of Object.entries(this.marks)) {
      if (this.round > item.last || complete) {
        item.mark.updateOpts({
          image: `assets/ui/match2/magiclamps/magiclamps_check_${checkpoint}.png`,
          width: 27,
          height: 27,
          centerAnchor: true,
          centerOnOrigin: true,
        });
      } else {
        item.mark.updateOpts({
          image: `assets/ui/match2/magiclamps/magiclamps_mark_${checkpoint}.png`,
          width: 50,
          height: 39,
          centerAnchor: true,
          centerOnOrigin: true,
          scale: this.round < item.first ? 0.75 : 1,
        });
      }
    }
  }

  public async animateToNextRound(round: number) {
    const nextRound = round;
    const nextRatio = this.roundToRatio(nextRound);
    await magicLampsAnimEx(this, { ratio: nextRatio }, 300);
    this.round = nextRound;
  }

  public async animateComplete() {
    // Overflow the bar by a round to make it reach the final prize
    const nextRatio = this.roundToRatio(this.numRounds + 1);
    await magicLampsAnimEx(this, { ratio: nextRatio }, 300);
    this.updateMarks(true);
  }
}
